import { useNavigate, Link } from "react-router-dom";
import firebase from "firebase/compat/app";

import style from "./signInForm.module.css";
import { useState, useEffect, useRef, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import ResetPassword from "../reset-password/reset-password";
import { Button, Grid, SegmentedControl, Text, Flex, Select, Box, Divider } from "@mantine/core";
import GoogleIcon from "../../assets/googleUserLogo.png";
import { IconLogin2, IconPhone, IconArrowLeft, IconChevronDown, IconMail } from "@tabler/icons-react";
import { useMediaQuery } from "@mantine/hooks";
import LastUsedBadge, { getLastSignInMethod } from "../../molecules/last-used-badge/lastUsedBadge";

// Country codes - primary markets first, then all European countries alphabetically
const COUNTRY_CODES = [
    // Primary markets (matching domains)
    { value: "+47", label: "+47", flag: "🇳🇴" },
    { value: "+46", label: "+46", flag: "🇸🇪" },
    { value: "+45", label: "+45", flag: "🇩🇰" },
    { value: "+44", label: "+44", flag: "🇬🇧" },
    { value: "+358", label: "+358", flag: "🇫🇮" },
    { value: "+49", label: "+49", flag: "🇩🇪" },
    { value: "+43", label: "+43", flag: "🇦🇹" },
    { value: "+31", label: "+31", flag: "🇳🇱" },
    { value: "+32", label: "+32", flag: "🇧🇪" },
    { value: "+34", label: "+34", flag: "🇪🇸" },
    { value: "+39", label: "+39", flag: "🇮🇹" },
    { value: "+33", label: "+33", flag: "🇫🇷" },

    { value: "+1", label: "+1", flag: "🇺🇸" },

    // Remaining European countries
    { value: "+355", label: "+355", flag: "🇦🇱" },
    { value: "+376", label: "+376", flag: "🇦🇩" },
    { value: "+375", label: "+375", flag: "🇧🇾" },
    { value: "+387", label: "+387", flag: "🇧🇦" },
    { value: "+359", label: "+359", flag: "🇧🇬" },
    { value: "+385", label: "+385", flag: "🇭🇷" },
    { value: "+357", label: "+357", flag: "🇨🇾" },
    { value: "+420", label: "+420", flag: "🇨🇿" },
    { value: "+372", label: "+372", flag: "🇪🇪" },
    { value: "+30", label: "+30", flag: "🇬🇷" },
    { value: "+36", label: "+36", flag: "🇭🇺" },
    { value: "+354", label: "+354", flag: "🇮🇸" },
    { value: "+353", label: "+353", flag: "🇮🇪" },
    { value: "+383", label: "+383", flag: "🇽🇰" },
    { value: "+371", label: "+371", flag: "🇱🇻" },
    { value: "+423", label: "+423", flag: "🇱🇮" },
    { value: "+370", label: "+370", flag: "🇱🇹" },
    { value: "+352", label: "+352", flag: "🇱🇺" },
    { value: "+356", label: "+356", flag: "🇲🇹" },
    { value: "+373", label: "+373", flag: "🇲🇩" },
    { value: "+377", label: "+377", flag: "🇲🇨" },
    { value: "+382", label: "+382", flag: "🇲🇪" },
    { value: "+389", label: "+389", flag: "🇲🇰" },
    { value: "+48", label: "+48", flag: "🇵🇱" },
    { value: "+351", label: "+351", flag: "🇵🇹" },
    { value: "+40", label: "+40", flag: "🇷🇴" },
    { value: "+7", label: "+7", flag: "🇷🇺" },
    { value: "+378", label: "+378", flag: "🇸🇲" },
    { value: "+381", label: "+381", flag: "🇷🇸" },
    { value: "+421", label: "+421", flag: "🇸🇰" },
    { value: "+386", label: "+386", flag: "🇸🇮" },
    { value: "+41", label: "+41", flag: "🇨🇭" },
    { value: "+90", label: "+90", flag: "🇹🇷" },
    { value: "+380", label: "+380", flag: "🇺🇦" },
    { value: "+379", label: "+379", flag: "🇻🇦" },
];

// Custom Select item component to display flag alongside the label
const CountrySelectItem = forwardRef(({ label, flag, ...others }, ref) => (
    <div ref={ref} {...others}>
        <span style={{ marginRight: "8px" }}>{flag}</span>
        {label}
    </div>
));

// Get default country code based on domain
const getDefaultCountryCode = () => {
    const hostname = window.location.hostname.toLowerCase();
    if (hostname.includes("verklig.ai")) return "+46"; // Sweden
    if (hostname.includes("virkelig.ai")) return "+45"; // Denmark
    if (hostname.includes("ekte.ai")) return "+47"; // Norway
    if (hostname.includes("aidetectors.es")) return "+34"; // Spain
    if (hostname.includes("aidetectors.fr")) return "+33"; // France
    if (hostname.includes("aidetectors.de")) return "+49"; // Germany
    if (hostname.includes("aidetectors.at")) return "+43"; // Austria
    if (hostname.includes("werkelijk.ai")) return "+31"; // Netherlands
    if (hostname.includes("aidetectors.be")) return "+32"; // Belgium
    if (hostname.includes("gradr.ai")) return "+44"; // UK
    return "+47"; // Default to Norway
};

const SignInForm = ({ auth, nextPageUrl, displayToggleSignUpOrIn=true }) => {
    const { t, i18n } = useTranslation(["common", "landing-page"]);

    const navigate = useNavigate();

    const isMetaBrowser = navigator.userAgent.match(/FBAN|FBAV/i); //True if browser is Facebook or Messenger app browser

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [error, setError] = useState("");

    // Read once on mount so the badge does not move while signing in
    const [lastSignInMethod] = useState(getLastSignInMethod);

    // Phone auth states - initialize from localStorage preference
    const [authMode, setAuthMode] = useState(() => {
        const savedPreference = localStorage.getItem("signInPreference");
        return savedPreference === "phone" ? "phone" : "email";
    });
    const [countryCode, setCountryCode] = useState(getDefaultCountryCode);
    const [phoneNumber, setPhoneNumber] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [phoneLoading, setPhoneLoading] = useState(false);
    const recaptchaVerifierRef = useRef(null);
    const recaptchaWidgetIdRef = useRef(null);

    // Initialize reCAPTCHA verifier
    useEffect(() => {
        if (authMode === "phone" && !recaptchaVerifierRef.current) {
            try {
                firebase.auth().languageCode = i18n.language;
                recaptchaVerifierRef.current = new firebase.auth.RecaptchaVerifier("recaptcha-container-signin", {
                    size: "normal",
                    callback: (response) => {
                        // reCAPTCHA solved - allow send verification code
                        console.log("reCAPTCHA verified");
                    },
                    "expired-callback": () => {
                        // Reset reCAPTCHA
                        setError(t("auth.errors.recaptcha-expired") || "reCAPTCHA expired. Please try again.");
                        if (recaptchaVerifierRef.current) {
                            recaptchaVerifierRef.current.render().then((widgetId) => {
                                recaptchaWidgetIdRef.current = widgetId;
                            });
                        }
                    },
                });

                recaptchaVerifierRef.current.render().then((widgetId) => {
                    recaptchaWidgetIdRef.current = widgetId;
                });
            } catch (error) {
                console.error("reCAPTCHA initialization error:", error);
            }
        }

        return () => {
            if (recaptchaVerifierRef.current && authMode !== "phone") {
                try {
                    recaptchaVerifierRef.current.clear();
                    recaptchaVerifierRef.current = null;
                } catch (e) {
                    // Ignore cleanup errors
                }
            }
        };
    }, [authMode]);

    const signInWithGoogle = async () => {
        const provider = new firebase.auth.GoogleAuthProvider();
        //auth.signInWithPopup(provider);

        try {
            const result = await auth.signInWithPopup(provider);
            // If sign-in is successful, navigate to the "/join" page
            if (result.user) {
                localStorage.setItem("signInPreference", "google");
                setPersistence();
                navigate(nextPageUrl);
            }
        } catch (error) {
            // Handle any errors that might occur during sign-in
            console.error("Error signing in with Google:", error.message);
        }

        // setPersistence()
        // navigate(nextPageUrl)
    };

    const signInWithEmail = async () => {
        setError("");

        await firebase
            .auth()
            .signInWithEmailAndPassword(email, password)
            .then((userCredential) => {
                // Signed in - save preference
                localStorage.setItem("signInPreference", "email");
            })
            .catch((error) => {
                setErrorMessage(error);
            });

        setPersistence();
        navigate(nextPageUrl);
    };

    // Phone authentication - Send verification code
    const sendVerificationCode = async () => {
        if (!phoneNumber || phoneNumber.length < 8) {
            setError(t("auth.errors.invalid-phone") || "Please enter a valid phone number.");
            return;
        }

        setError("");
        setPhoneLoading(true);

        try {
            // Format phone number with selected country code
            let formattedPhone = phoneNumber.trim().replace(/\s/g, "");
            // Remove leading zero if present (common in local formats)
            if (formattedPhone.startsWith("0")) {
                formattedPhone = formattedPhone.substring(1);
            }
            formattedPhone = countryCode + formattedPhone;

            const appVerifier = recaptchaVerifierRef.current;
            const result = await firebase.auth().signInWithPhoneNumber(formattedPhone, appVerifier);

            setConfirmationResult(result);
            setIsCodeSent(true);
            setPhoneLoading(false);
        } catch (error) {
            console.error("Error sending verification code:", error);
            setPhoneLoading(false);
            setPhoneErrorMessage(error);

            // Reset reCAPTCHA on error
            if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
                window.grecaptcha.reset(recaptchaWidgetIdRef.current);
            }
        }
    };

    // Phone authentication - Verify code
    const verifyCode = async () => {
        if (!verificationCode || verificationCode.length < 6) {
            setError(t("auth.errors.invalid-code") || "Please enter the 6-digit verification code.");
            return;
        }

        setError("");
        setPhoneLoading(true);

        try {
            await confirmationResult.confirm(verificationCode);

            // Save preference
            localStorage.setItem("signInPreference", "phone");

            setPersistence();
            setPhoneLoading(false);
            navigate(nextPageUrl);
        } catch (error) {
            console.error("Error verifying code:", error);
            setPhoneLoading(false);
            setPhoneErrorMessage(error);
        }
    };

    const setPhoneErrorMessage = (error) => {
        if (error.code === "auth/invalid-phone-number") setError(t("auth.errors.invalid-phone") || "Invalid phone number format.");
        else if (error.code === "auth/too-many-requests") setError(t("auth.errors.too-many-requests") || "Too many attempts. Please try again later.");
        else if (error.code === "auth/invalid-verification-code") setError(t("auth.errors.invalid-code") || "Invalid verification code.");
        else if (error.code === "auth/code-expired") setError(t("auth.errors.code-expired") || "Verification code has expired. Please request a new one.");
        else if (error.code === "auth/invalid-app-credential") setError(t("auth.errors.invalid-app-credential") || "reCAPTCHA verification failed or expired. Please complete the reCAPTCHA again.");
        else setError(error.message);
    };

    const resetPhoneAuth = () => {
        setIsCodeSent(false);
        setVerificationCode("");
        setConfirmationResult(null);
        setError("");
    };

    const setPersistence = async () => {
        await firebase.auth().setPersistence(firebase.auth.Auth.Persistence.LOCAL);
    };

    const setErrorMessage = (error) => {
        if (error.code === "auth/email-already-exists") setError(t("auth.errors.email-already-exists"));
        else if (error.code === "auth/user-not-found") setError(t("auth.errors.user-not-found"));
        else if (error.code === "auth/email-already-in-use") setError(t("auth.errors.email-already-exists"));
        else if (error.code === "auth/wrong-password") setError(t("auth.errors.wrong-password"));
        else if (error.code === "auth/weak-password") setError(t("auth.errors.weak-password"));
        else if (error.code === "auth/user-disabled") setError(t("auth.errors.user-disabled"));
        else setError(error.message);
    };

    const validateEmail = (email) => {
        // Split the email into two parts: the username and domain
        const emailParts = email.split("@");
        if (emailParts.length !== 2) {
            return false; // email should have only one @ symbol
        }

        const username = emailParts[0];
        const domain = emailParts[1];

        // Check if the domain has a valid format
        const domainParts = domain.split(".");
        if (domainParts.length < 2) {
            return false; // domain should have at least one dot
        }

        for (let i = 0; i < domainParts.length; i++) {
            if (domainParts[i].length < 2) {
                return false; // each domain part should have at least two characters
            }
        }

        // Check if the username has a valid format
        if (username.length < 1) {
            return false; // username should not be empty
        }

        return true;
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" && validateEmail(email)) {
            signInWithEmail();
        }
    };

    const isInMobileView = useMediaQuery("(max-width: 800px)");

    return (
        <div className={style.contentWrapper}>
            <h2 className={style.header}>{t("auth.sign-in.form.title")}</h2>

            <div className={style.contentGroup}>
                <div className={style.actionGroup}>
                    <div className={style.actionItem}>
                        <Button
                            onClick={signInWithGoogle}
                            variant="filled"
                            color="dark"
                            size="md"
                            w={"16rem"}
                            radius={"xl"}
                            leftIcon={<img className={style.googleIcon} src={GoogleIcon} alt="" />}
                        >
                            {`${t("auth.sign-in.form.sign-in-with-google")} →`}
                        </Button>
                        {lastSignInMethod === "google" && <LastUsedBadge />}
                        <br />
                        {isMetaBrowser && <span style={{ fontSize: "14px" }}>{t("auth.meta-browser-warning")}</span>}
                    </div>
                </div>
            </div>

            <Divider mt={"2rem"} mb={"1rem"} />

            {/* Auth mode selector */}
            <SegmentedControl
                value={authMode}
                onChange={(value) => {
                    setAuthMode(value);
                    setError("");
                    setIsCodeSent(false);
                    setVerificationCode("");
                }}
                color="dark"
                radius={"2rem"}
                size="lg"
                mt="md"
                mb="md"
                data={[
                    { label: <Flex w={"100%"} justify={"center"} align={"center"} gap={"0.5rem"}><IconMail size={16} /><Box fz={"1rem"}>{t("auth.sign-in.form.tab-email") || "Email"}</Box> </Flex>, value: "email" },
                    { label: <Flex w={"100%"} justify={"center"} align={"center"} gap={"0.5rem"}><IconPhone size={16} /><Box fz={"1rem"}>{t("auth.sign-in.form.tab-phone") || "Phone"}</Box> </Flex>, value: "phone" },
                ]}
                styles={{
                    root: { backgroundColor: "white" },
                    label: { fontWeight: 500 },
                }}
            />

            {/* Email sign-in form */}
            {authMode === "email" && (
                <div className={style.signInGroup}>
                    <input
                        autoFocus={!isInMobileView}
                        type="email"
                        placeholder={t("auth.sign-in.form.email-label")}
                        className={style.input}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={handleKeyDown}
                    ></input>
                    <input
                        type="password"
                        placeholder={t("auth.sign-in.form.password-label")}
                        className={style.input}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyDown={handleKeyDown}
                    ></input>

                    {error && <p className={style.errorMessage}>{error}</p>}

                    <Grid w={"20rem"} gutter={"1rem"} mt={"0.5rem"} align={"center"}>
                        <Grid.Col span={12}>
                            <Button
                                leftIcon={<IconLogin2 size={20} />}
                                w={"16rem"}
                                size="md"
                                radius={"xl"}
                                color="dark"
                                onClick={signInWithEmail}
                                disabled={!validateEmail(email) || password === ""}
                            >
                                {t("auth.sign-in.form.submit")}
                            </Button>
                            {lastSignInMethod === "email" && <LastUsedBadge />}
                        </Grid.Col>
                    </Grid>

                    <div style={{ marginTop: "2rem" }}>
                        <ResetPassword auth={auth} />
                    </div>
                </div>
            )}

            {/* Phone sign-in form */}
            {authMode === "phone" && (
                <div className={style.signInGroup}>
                    {!isCodeSent ? (
                        <>
                            <Text size="sm" color="dimmed" mb="sm" ta="center">
                                {t("auth.sign-in.form.phone-description") || "Enter your phone number to receive a verification code via SMS."}
                            </Text>

                            <div className={style.phoneInputWrapper}>
                                <Select
                                    data={COUNTRY_CODES}
                                    value={countryCode}
                                    searchable
                                    onChange={setCountryCode}
                                    itemComponent={CountrySelectItem}
                                    icon={COUNTRY_CODES.find((c) => c.value === countryCode)?.flag}
                                    className={style.countryCodeSelect}
                                    rightSection={<IconChevronDown size={14} />}
                                    styles={{
                                        input: {
                                            border: "1px solid var(--border)",
                                            borderRadius: "0.2rem",
                                            fontFamily: "'Segoe UI', system-ui, sans-serif",
                                            fontSize: "16px",
                                            height: "auto",
                                            minHeight: "unset",
                                            padding: "0.7rem",
                                            paddingLeft: "2.5rem",
                                            paddingRight: "1.5rem",
                                        },
                                        rightSection: {
                                            pointerEvents: "none",
                                        },
                                        icon: {
                                            fontSize: "1.2rem",
                                        },
                                    }}
                                    withinPortal
                                />
                                <input
                                    autoFocus={!isInMobileView}
                                    type="tel"
                                    placeholder={t("auth.sign-in.form.phone-number-placeholder") || "123 45 678"}
                                    className={`${style.input} ${style.phoneNumberInput}`}
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && phoneNumber.length >= 8) {
                                            sendVerificationCode();
                                        }
                                    }}
                                />
                            </div>

                            <Flex justify="center" mt="md" mb="sm" m={"auto"} h={"76px"} w={"302px"} bg={"#EAEAEA"}>
                                <div id="recaptcha-container-signin"></div>
                            </Flex>

                            {error && <p className={style.errorMessage}>{error}</p>}

                            <Grid w={"20rem"} gutter={"1rem"} mt={"0.5rem"} align={"center"}>
                                <Grid.Col span={12}>
                                    <Button
                                        leftIcon={<IconPhone size={20} />}
                                        w={"16rem"}
                                        size="md"
                                        radius={"xl"}
                                        color="dark"
                                        onClick={sendVerificationCode}
                                        disabled={phoneNumber.length < 8}
                                        loading={phoneLoading}
                                        className="plausible-event-name=Signin-Phone"
                                    >
                                        {t("auth.sign-in.form.send-code") || "Send Verification Code"}
                                    </Button>
                                    {lastSignInMethod === "phone" && <LastUsedBadge />}
                                </Grid.Col>
                            </Grid>
                        </>
                    ) : (
                        <>
                            <Button variant="subtle" color="dark" leftIcon={<IconArrowLeft size={16} />} onClick={resetPhoneAuth} mb="sm" size="xs">
                                {t("auth.sign-in.form.change-number") || "Change number"}
                            </Button>

                            <Text size="sm" color="dimmed" mb="sm" ta="center">
                                {t("auth.sign-in.form.code-sent") || "A verification code has been sent to"} {countryCode} {phoneNumber}
                            </Text>

                            <input
                                autoFocus
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={6}
                                placeholder={t("auth.sign-in.form.code-label") || "Enter 6-digit code"}
                                className={style.input}
                                value={verificationCode}
                                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && verificationCode.length === 6) {
                                        verifyCode();
                                    }
                                }}
                                style={{ textAlign: "center", letterSpacing: "0.5rem", fontSize: "1.25rem" }}
                            ></input>

                            {error && <p className={style.errorMessage}>{error}</p>}

                            <Grid w={"20rem"} gutter={"1rem"} mt={"0.5rem"} align={"center"}>
                                <Grid.Col span={12}>
                                    <Button
                                        leftIcon={<IconLogin2 size={20} />}
                                        w={"16rem"}
                                        size="md"
                                        radius={"xl"}
                                        color="dark"
                                        onClick={verifyCode}
                                        disabled={verificationCode.length !== 6}
                                        loading={phoneLoading}
                                        className="plausible-event-name=Signin-Phone-Verify"
                                    >
                                        {t("auth.sign-in.form.verify-code") || "Verify & Sign In"}
                                    </Button>
                                </Grid.Col>
                            </Grid>
                        </>
                    )}
                </div>
            )}

            {displayToggleSignUpOrIn && <Link to="/sign-up" className={style.linkCreateUser}>
                <p className={style.link}>{t("auth.sign-in.form.switch-to-create-user-link")}</p>
            </Link>}
        </div>
    );
};

export default SignInForm;
